import React, { useEffect, useState } from 'react'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Company, IdBoolMap, PlanStatus } from '../../models'
import { PropertyRow } from './PropertyRow'
import { ApartmentVendorChannel } from './NationalPropertyList'

interface Props {
    properties: Company[]
    vendorPlan: string
    setSelectedProperty: (property: Company) => void
    setMessageDrawerOpen: (open: boolean) => void
    apartmentVendorChannels: ApartmentVendorChannel[]
    setApartmentVendorChannels: (
        aptVenChannel: ApartmentVendorChannel[],
    ) => void
    unlockedPropertyMap: IdBoolMap
    setOpenBuyProperty: (open: boolean) => void
    setOpenPropertyDetail: (open: boolean) => void
}

export type PropertyRowType = {
    id: number
    name: string
    address: string
    phoneNumber: string | null
    email?: string
    city: string
    state: string
    property: Company
}

const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        flex: 1,
        maxHeight: 'calc(100vh - 380px)',
        overflow: 'auto',
    },
    table: {
        minWidth: 900,
    },
    tableHead: {
        backgroundColor: '#f8fafc',
        '& .MuiTableCell-root': {
            fontWeight: 600,
            color: theme.palette.text.secondary,
            fontSize: '13px',
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
}))

type OrderKey = 'name' | 'address' | 'city' | 'state'

export const NatPropListTable = (props: Props) => {
    const {
        properties,
        setSelectedProperty,
        vendorPlan,
        setMessageDrawerOpen,
        apartmentVendorChannels,
        setApartmentVendorChannels,
        unlockedPropertyMap,
        setOpenBuyProperty,
        setOpenPropertyDetail,
    } = props

    const classes = useStyles()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [orderBy, setOrderBy] = useState<OrderKey>('name')
    const [order, setOrder] = useState<'asc' | 'desc'>('asc')

    const createPropertyRow = (property: Company): PropertyRowType => ({
        id: property.id,
        name: property.name,
        address: property.address,
        phoneNumber: property.phone,
        email: property.email,
        city: property.city,
        state: property.state,
        property,
    })

    const [sortedRows, setSortedRows] = useState<PropertyRowType[]>([])

    useEffect(() => {
        setSortedRows(properties.map(createPropertyRow))
    }, [properties])

    const handleSort = (key: OrderKey) => {
        const isAsc = orderBy === key && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(key)
        setSortedRows((prev) =>
            [...prev].sort((a, b) => {
                const aVal = a[key]
                const bVal = b[key]
                const cmp = String(aVal).localeCompare(String(bVal))
                return isAsc ? -cmp : cmp
            }),
        )
    }

    const paginatedRows = sortedRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
    )

    return (
        <Box display="flex" flexDirection="column" className={classes.wrapper}>
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader size="small">
                    <TableHead>
                        <TableRow className={classes.tableHead}>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={
                                        orderBy === 'name' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'address'}
                                    direction={
                                        orderBy === 'address' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('address')}
                                >
                                    Address
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'city'}
                                    direction={
                                        orderBy === 'city' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('city')}
                                >
                                    City
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'state'}
                                    direction={
                                        orderBy === 'state' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('state')}
                                >
                                    State
                                </TableSortLabel>
                            </TableCell>
                            {vendorPlan === PlanStatus.PREMIUM && (
                                <TableCell align="center">Message</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows.map((row) => (
                            <PropertyRow
                                key={row.id}
                                row={row}
                                setSelectedProperty={setSelectedProperty}
                                vendorPlan={vendorPlan}
                                setMessageDrawerOpen={setMessageDrawerOpen}
                                apartmentVendorChannels={
                                    apartmentVendorChannels
                                }
                                setApartmentVendorChannels={
                                    setApartmentVendorChannels
                                }
                                unlockedPropertyMap={unlockedPropertyMap}
                                setOpenBuyProperty={setOpenBuyProperty}
                                setOpenPropertyDetail={setOpenPropertyDetail}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                onChangePage={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50]}
            />
        </Box>
    )
}
